<script setup lang="ts">
import { computed, onMounted, ref, nextTick, watch, onBeforeUnmount } from 'vue'

interface PathDef {
  id: number
  d: string
  width: number
  baseOpacity: number
  duration: number
}

const props = withDefaults(defineProps<{
  // Visual density and look
  count?: number
  segmentsPerPath?: number
  batonLengthRatio?: number
  widthBase?: number
  widthIncrement?: number
  baseOpacity?: number
  opacityIncrement?: number
  colorClass?: string
  // Motion
  speed?: number // 1 = default, 2 = faster, 0.5 = slower
  // Geometry (viewBox-based coordinates)
  viewW?: number
  viewH?: number
  startX?: number
  startY?: number
  endX?: number
  endY?: number
  curveOffset?: number // px offset of control points perpendicular to line
  spread?: number // px offset between tracks perpendicular to line
  mirrored?: boolean // render mirrored group on opposite side of normal
  curveSegments?: number // how many cubic segments to stitch together
  outflowMargin?: number // extend start/end beyond viewBox along tangent
  // Interaction
  interactive?: boolean
}>(), {
  count: 24,
  segmentsPerPath: 3,
  batonLengthRatio: 0.22,
  widthBase: 0.6,
  widthIncrement: 0.03,
  baseOpacity: 0.12,
  opacityIncrement: 0.01,
  colorClass: 'text-slate-200/60',
  speed: 1,
  viewW: 696,
  viewH: 316,
  startX: 12,
  startY: 20,
  endX: 684,
  endY: 296,
  curveOffset: 60,
  spread: 6,
  mirrored: true,
  curveSegments: 2,
  outflowMargin: 180,
  interactive: false,
})

const half = computed(() => Math.max(4, Math.floor(props.count / 2)))

// Interactive state initialized from props
const sx = ref(props.startX)
const sy = ref(props.startY)
const ex = ref(props.endX)
const ey = ref(props.endY)
const curve = ref(props.curveOffset)
const segCount = computed(() => Math.max(1, Math.floor(props.curveSegments)))
const curveOffsets = ref<number[]>(Array.from({ length: segCount.value }, () => props.curveOffset))

watch(() => [props.startX, props.startY, props.endX, props.endY, props.curveOffset], ([nx, ny, nex, ney, nc]) => {
  sx.value = nx
  sy.value = ny
  ex.value = nex
  ey.value = ney
  curve.value = nc
})

watch(segCount, (n) => {
  const old = curveOffsets.value
  const next = new Array(n).fill(0).map((_, i) => old[i] ?? curve.value)
  curveOffsets.value = next
})

watch(() => props.curveOffset, (v) => {
  // If user isn't actively dragging a curve handle, update all to new default
  if (dragging.value !== 'curve') {
    curveOffsets.value = curveOffsets.value.map(() => v)
  }
})

function makePathD(i: number, normalSign: number) {
  const N = half.value
  const t = N > 1 ? (i - (N - 1) / 2) : 0 // centered index
  const dx = ex.value - sx.value
  const dy = ey.value - sy.value
  const len = Math.hypot(dx, dy) || 1
  const ux = dx / len
  const uy = dy / len
  const nx = -uy
  const ny = ux
  const spread = props.spread * t
  const ox = nx * spread * normalSign
  const oy = ny * spread * normalSign
  // Extend start/end beyond viewBox along the tangent to allow full in/out flow
  const sxExt = sx.value - ux * props.outflowMargin
  const syExt = sy.value - uy * props.outflowMargin
  const exExt = ex.value + ux * props.outflowMargin
  const eyExt = ey.value + uy * props.outflowMargin
  const startX = sxExt + ox
  const startY = syExt + oy
  const endX = exExt + ox
  const endY = eyExt + oy
  const segs = segCount.value
  let d = `M${startX} ${startY}`
  for (let k = 0; k < segs; k++) {
    const t0 = k / segs
    const t1 = (k + 1) / segs
    const p0x = startX + (endX - startX) * t0
    const p0y = startY + (endY - startY) * t0
    const p3x = startX + (endX - startX) * t1
    const p3y = startY + (endY - startY) * t1
    const segLen = Math.hypot(p3x - p0x, p3y - p0y) || 1
    const cOffset = (curveOffsets.value[k] ?? curve.value) * normalSign
    const c1x = p0x + ux * (segLen * 0.33) + nx * cOffset
    const c1y = p0y + uy * (segLen * 0.33) + ny * cOffset
    const c2x = p0x + ux * (segLen * 0.66) + nx * cOffset
    const c2y = p0y + uy * (segLen * 0.66) + ny * cOffset
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p3x} ${p3y}`
  }
  return d
}

function makePaths(normalSign: number): PathDef[] {
  const N = half.value
  return Array.from({ length: N }, (_, i) => {
    const width = props.widthBase + i * props.widthIncrement
    const baseOpacity = Math.min(0.35, props.baseOpacity + i * props.opacityIncrement)
    const baseDuration = 16 + (i % 8)
    const duration = Math.max(4, baseDuration / Math.max(0.1, props.speed))
    return {
      id: i,
      d: makePathD(i, normalSign),
      width,
      baseOpacity,
      duration,
    }
  })
}

const leftPaths = computed(() => makePaths(1))
const rightPaths = computed(() => (props.mirrored ? makePaths(-1) : []))

// After render, compute lengths and set dash animation values
const rootEl = ref<HTMLElement | null>(null)
const overlayRef = ref<SVGSVGElement | null>(null)

async function setupAnim() {
  await nextTick()
  const els = Array.from(rootEl.value?.querySelectorAll('path.path-anim') || []) as SVGPathElement[]
  for (const el of els) {
    const len = el.getTotalLength()
    const seg = Math.max(40, len * props.batonLengthRatio)
    el.style.setProperty('--len', `${len}px`)
    el.style.setProperty('--seg', `${seg}px`)
    // Use an oversized gap so the dash pattern never wraps back to the start within one cycle
    // This prevents a tail sticking out at the beginning when the dash crosses the end cap.
    el.style.strokeDasharray = `${seg} ${Math.max(1, Math.ceil(len * 4))}`
    const duration = parseFloat(el.dataset.duration || '16')
    const segIndex = parseInt(el.dataset.seg || '0')
    const totalSegs = Math.max(1, props.segmentsPerPath || 1)
    el.style.animationDuration = `${duration}s`
    el.style.animationDelay = `${(segIndex / totalSegs) * duration}s`
  }
}

onMounted(() => {
  setupAnim()
})

watch(() => [leftPaths.value, rightPaths.value, props.segmentsPerPath, props.batonLengthRatio], () => {
  setupAnim()
})

// Interactive dragging
type DragTarget = 'start' | 'end' | 'curve' | null
const dragging = ref<DragTarget>(null)
const draggingCurveIndex = ref(-1)
const curveHandle = computed(() => {
  const dx = ex.value - sx.value
  const dy = ey.value - sy.value
  const len = Math.hypot(dx, dy) || 1
  const nx = -dy / len
  const ny = dx / len
  const mx = (sx.value + ex.value) / 2
  const my = (sy.value + ey.value) / 2
  return { x: mx + nx * curve.value, y: my + ny * curve.value }
})

function clamp(v: number, min: number, max: number) { return Math.min(Math.max(v, min), max) }

function onMove(e: MouseEvent) {
  const box = overlayRef.value?.getBoundingClientRect()
  if (!box) return
  const x = ((e.clientX - box.left) / box.width) * props.viewW
  const y = ((e.clientY - box.top) / box.height) * props.viewH
  if (dragging.value === 'start') {
    sx.value = clamp(x, 0, props.viewW)
    sy.value = clamp(y, 0, props.viewH)
  } else if (dragging.value === 'end') {
    ex.value = clamp(x, 0, props.viewW)
    ey.value = clamp(y, 0, props.viewH)
  } else if (dragging.value === 'curve') {
    // Adjust selected curve segment k perpendicular to path at midline
    const k = draggingCurveIndex.value
    if (k >= 0) {
      const dx = ex.value - sx.value
      const dy = ey.value - sy.value
      const len = Math.hypot(dx, dy) || 1
      const nx = -dy / len
      const ny = dx / len
      const mx = (sx.value + ex.value) / 2
      const my = (sy.value + ey.value) / 2
      const vx = x - mx
      const vy = y - my
      const proj = vx * nx + vy * ny
      curveOffsets.value[k] = clamp(proj, -Math.max(props.viewW, props.viewH), Math.max(props.viewW, props.viewH))
    }
  }
}

function endDrag() { dragging.value = null; draggingCurveIndex.value = -1; window.removeEventListener('mousemove', onMove as any); window.removeEventListener('mouseup', endDrag) }
function startDrag(target: DragTarget, e: MouseEvent, idx?: number) {
  dragging.value = target
  if (target === 'curve' && typeof idx === 'number') draggingCurveIndex.value = idx
  window.addEventListener('mousemove', onMove as any)
  window.addEventListener('mouseup', endDrag)
  onMove(e) // update immediately to avoid initial jump
}

onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onMove as any)
  window.removeEventListener('mouseup', endDrag)
})
</script>

<template>
  <div ref="rootEl" class="absolute inset-0" :class="props.interactive ? 'pointer-events-auto' : 'pointer-events-none'">
    <!-- Left group -->
    <svg class="w-full h-full" :viewBox="`0 0 ${props.viewW} ${props.viewH}`" fill="none" aria-hidden="true" :class="colorClass">
      <template v-for="path in leftPaths" :key="`l-${path.id}`">
        <!-- Base subtle visible path -->
        <path :d="path.d" stroke="currentColor" :stroke-width="path.width" :opacity="path.baseOpacity" />
        <!-- Animated flowing segment overlays -->
        <path
          v-for="s in props.segmentsPerPath"
          :key="`ls-${path.id}-${s}`"
          :d="path.d"
          stroke="currentColor"
          :stroke-width="path.width"
          class="path-anim"
          :data-seg="s - 1"
          :data-duration="path.duration"
          :data-group="`l-${path.id}`"
          :ref="setAnimRef"
        />
      </template>
    </svg>

    <!-- Right group -->
    <svg class="absolute inset-0 w-full h-full" :viewBox="`0 0 ${props.viewW} ${props.viewH}`" fill="none" aria-hidden="true" :class="colorClass">
      <template v-for="path in rightPaths" :key="`r-${path.id}`">
        <path :d="path.d" stroke="currentColor" :stroke-width="path.width" :opacity="path.baseOpacity" />
        <path
          v-for="s in props.segmentsPerPath"
          :key="`rs-${path.id}-${s}`"
          :d="path.d"
          stroke="currentColor"
          :stroke-width="path.width"
          class="path-anim"
          :data-seg="s - 1"
          :data-duration="path.duration"
          :data-group="`r-${path.id}`"
          :ref="setAnimRef"
        />
      </template>
    </svg>
    <!-- Interactive handles -->
    <svg v-if="props.interactive" ref="overlayRef" class="absolute inset-0 w-full h-full" :viewBox="`0 0 ${props.viewW} ${props.viewH}`">
      <line :x1="sx" :y1="sy" :x2="ex" :y2="ey" stroke="currentColor" stroke-opacity="0.15" stroke-width="1" />
      <circle :cx="sx" :cy="sy" r="6" class="cursor-move" fill="currentColor" fill-opacity="0.6" @mousedown.prevent.stop="startDrag('start', $event)" />
      <circle :cx="ex" :cy="ey" r="6" class="cursor-move" fill="currentColor" fill-opacity="0.6" @mousedown.prevent.stop="startDrag('end', $event)" />
      <!-- Multiple curve handles along path midline; drag perpendicular to adjust each segment offset -->
      <template v-for="k in segCount" :key="`handle-${k-1}`">
        <circle
          :cx="(sx+ex)/2 - (ey - sy)/Math.hypot(ex - sx, ey - sy) * (curveOffsets[k-1] || 0)"
          :cy="(sy+ey)/2 + (ex - sx)/Math.hypot(ex - sx, ey - sy) * (curveOffsets[k-1] || 0)"
          r="5"
          class="cursor-alias"
          fill="currentColor"
          fill-opacity="0.8"
          @mousedown.prevent.stop="startDrag('curve', $event, k-1)"
        />
      </template>
    </svg>
  </div>
</template>

<style>
@keyframes path-flow {
  0% { stroke-dasharray: 0 var(--len); stroke-dashoffset: 0; }
  2% { stroke-dasharray: var(--seg) calc(var(--len) - var(--seg)); stroke-dashoffset: 0; }
  100% { stroke-dasharray: var(--seg) calc(var(--len) - var(--seg)); stroke-dashoffset: calc(var(--len) + var(--seg)); }
}
.path-anim {
  stroke-linecap: round;
  /* animation set inline for per-path durations */
  animation-name: path-flow;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-fill-mode: both;
  will-change: stroke-dashoffset;
}
</style>
